/**
 * InquiryLog 组件 — 纯函数工具
 *
 * 源自 specs/001-inquiry-card-agent-log/data-model.md §2 / §2.5 + research.md R1 / R4。
 *
 * 本文件只包含**纯函数**，不依赖 React / i18next。分组标题的 `today` /
 * `yesterday` 种类判定放在这里，组件层再通过 i18n 映射为本地化文案。
 */

import dayjs from '@/utils/dayjs';
import type {
  IIMInquiryLogPayload,
  InquiryLogGroupView,
  InquiryLogItemView,
  InquiryLogSubStepLike,
  LogEvent,
} from './types';

// ---------------------------------------------------------------------------
// §1. 占位符渲染
// ---------------------------------------------------------------------------

const PLACEHOLDER_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

/**
 * 将非字符串值转为展示字符串。
 *
 * 规则（data-model §2 / research R1）：
 *  - string            → 原样
 *  - number / boolean  → String(value)
 *  - string[]          → value.join(', ')
 *  - object            → JSON.stringify(value)（极端兜底）
 *  - null / undefined  → null（视为缺失）
 */
function stringifyValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map(v => (v === null || v === undefined ? '' : typeof v === 'string' ? v : String(v)))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }
  return String(value);
}

/**
 * 使用 displayData 替换 displayText 中的 `{key}` 占位符，保留 `\n`。
 *
 * @param template 原始模板字符串
 * @param data     占位符数据源；允许为 null
 * @param fallback 缺失 key 时的行为：
 *                  - 'keep'  保留原占位符（默认，便于后端排查）
 *                  - 'empty' 替换为空串
 */
export function renderDisplayText(
  template: string,
  data: Record<string, unknown> | null | undefined,
  fallback: 'keep' | 'empty' = 'keep'
): string {
  if (!template) return '';
  const dict = data ?? {};

  return template.replace(PLACEHOLDER_RE, (match, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(dict, key)) {
      return fallback === 'empty' ? '' : match;
    }
    const resolved = stringifyValue(dict[key]);
    if (resolved === null) {
      return fallback === 'empty' ? '' : match;
    }
    return resolved;
  });
}

// ---------------------------------------------------------------------------
// §2. 时间格式化
// ---------------------------------------------------------------------------

/**
 * 格式化日志时间。
 *
 * @param startTime epoch ms
 * @param mode      'time' → 'HH:mm:ss'；'datetime' → 'MM/DD HH:mm'；'short' → 'HH:mm'
 */
export function formatLogTime(startTime: number, mode: 'time' | 'datetime' | 'short' = 'time'): string {
  if (!startTime || Number.isNaN(startTime)) return '';
  const d = dayjs(startTime);
  if (!d.isValid()) return '';
  if (mode === 'datetime') return d.format('MM/DD HH:mm');
  if (mode === 'short') return d.format('HH:mm');
  return d.format('HH:mm:ss');
}

/**
 * 将 step 字符串转为人类可读标签。
 *
 * 如果值已经是可读形式（如 "Sourcing"）直接返回；
 * 如果是 SNAKE_CASE（如 "SUPPLIER_SEARCH"）则转为 Title Case。
 */
export function humanizeStep(step: string): string {
  if (!step) return '';
  // 已经是可读形式（非全大写或不含下划线）
  if (step !== step.toUpperCase() || !step.includes('_')) {
    return step.charAt(0).toUpperCase() + step.slice(1);
  }
  // SNAKE_CASE → Title Case
  return step
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// ---------------------------------------------------------------------------
// §3. identity 与合并策略（data-model §2.5）
// ---------------------------------------------------------------------------

/**
 * 计算日志条目的唯一身份：
 *  - 优先使用后端下发的稳定 `templateId`；
 *  - 否则退回 `step::subStep::startTime` 组合键。
 */
export function logIdentity(item: { templateId?: string; step: string; subStep: string; startTime: number }): string {
  if (item.templateId) return item.templateId;
  return `${item.step}::${item.subStep}::${item.startTime}`;
}

/**
 * 将 IM 推送负载转换为与 REST 响应同形的列表元素。
 *
 * 注：保留 `inquiryId` / `templateId` 字段，方便后续 UPDATE 定位。
 */
function payloadToItem(payload: IIMInquiryLogPayload): InquiryLogSubStepLike {
  return {
    // IApiInquiryLogSubStepResp 对齐
    step: payload.step,
    subStep: payload.subStep,
    status: payload.status,
    displayText: payload.displayText,
    displayData: (payload.displayData ?? {}) as Record<string, unknown>,
    startTime: payload.startTime,
    endTime: payload.endTime ?? 0,
    // 扩展字段
    inquiryId: payload.inquiryId,
    templateId: payload.templateId,
  };
}

/**
 * 按插入策略决定 ADD 时新条目落在数组哪一端：
 *  - order='asc'  旧→新：新条目 append 到末尾
 *  - order='desc' 新→旧：新条目 prepend 到头部
 */
function appendByOrder(
  list: InquiryLogSubStepLike[],
  item: InquiryLogSubStepLike,
  order: 'asc' | 'desc'
): InquiryLogSubStepLike[] {
  return order === 'desc' ? [item, ...list] : [...list, item];
}

/**
 * 合并 IM 事件到现有列表（不可变）。
 *
 * 行为表（data-model §2.5）：
 *  | 事件               | 行为                                                      |
 *  | ADD（identity 未存在） | 按 order 追加                                           |
 *  | ADD（identity 已存在） | 降级为 UPDATE（就地替换）                                |
 *  | UPDATE（identity 存在） | 就地替换                                               |
 *  | UPDATE（identity 缺失） | append 兜底（避免消息乱序导致永久丢失）                  |
 *
 * @param list  现有列表
 * @param evt   ADD / UPDATE 事件
 * @param order 排序方向（仅影响 ADD / append fallback 的落点）
 */
export function applyLogEvent(
  list: InquiryLogSubStepLike[],
  evt: LogEvent,
  order: 'asc' | 'desc' = 'asc'
): InquiryLogSubStepLike[] {
  const incoming = payloadToItem(evt.payload);
  const incomingId = logIdentity(incoming);
  const idx = list.findIndex(item => logIdentity(item) === incomingId);

  if (evt.type === 'ADD') {
    if (idx >= 0) {
      // 降级为 UPDATE
      const next = list.slice();
      next[idx] = { ...list[idx], ...incoming };
      return next;
    }
    return appendByOrder(list, incoming, order);
  }

  // UPDATE
  if (idx >= 0) {
    const next = list.slice();
    next[idx] = { ...list[idx], ...incoming };
    return next;
  }
  // UPDATE 未命中 → append 兜底
  return appendByOrder(list, incoming, order);
}

// ---------------------------------------------------------------------------
// §4. 分组（ContactProgressPage 专用）
// ---------------------------------------------------------------------------

/**
 * 按 `YYYY-MM-DD` 分组并输出视图模型。
 *
 * - `groupTitle` 仅给出语义种类：
 *    - 今日 → 'today'
 *    - 昨日 → 'yesterday'
 *    - 其余 → `YYYY-MM-DD`
 *   组件层再通过 `t('inquiries.log.today' | 'yesterday')` 做本地化替换。
 * - 组内保持元素原顺序（与输入一致，由调用方决定 asc/desc）。
 * - 组之间按 `dateKey` 升序；若调用方需要降序，可在渲染层 reverse。
 */
export function groupLogsByDate(items: InquiryLogItemView[]): InquiryLogGroupView[] {
  if (!items?.length) return [];

  const today = dayjs().format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const map = new Map<string, InquiryLogItemView[]>();

  for (const item of items) {
    const key = dayjs(item.startTime).format('YYYY-MM-DD');
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      map.set(key, [item]);
    }
  }

  const groups: InquiryLogGroupView[] = [];
  for (const [dateKey, groupItems] of map.entries()) {
    let groupTitle: InquiryLogGroupView['groupTitle'];
    if (dateKey === today) groupTitle = 'today';
    else if (dateKey === yesterday) groupTitle = 'yesterday';
    else groupTitle = dateKey;
    groups.push({ dateKey, groupTitle, items: groupItems });
  }

  groups.sort((a, b) => (a.dateKey < b.dateKey ? -1 : a.dateKey > b.dateKey ? 1 : 0));
  return groups;
}

// ---------------------------------------------------------------------------
// §5. 将原始条目转为视图模型（便于组件层复用）
// ---------------------------------------------------------------------------

/**
 * 将单条 REST/IM 记录转为 UI 视图模型。
 */
export function toItemView(
  raw: InquiryLogSubStepLike,
  idx: number,
  options?: { fallbackMissing?: 'keep' | 'empty'; timeMode?: 'time' | 'datetime' | 'short' }
): InquiryLogItemView {
  const { fallbackMissing = 'keep', timeMode = 'time' } = options ?? {};
  return {
    key: `${raw.startTime}-${raw.step}-${raw.subStep}-${idx}`,
    status: raw.status,
    resolvedText: renderDisplayText(raw.displayText ?? '', raw.displayData ?? null, fallbackMissing),
    startTime: raw.startTime,
    displayTime: formatLogTime(raw.startTime, timeMode),
    stepLabel: humanizeStep(raw.step ?? ''),
    raw,
  };
}

/**
 * 将 YYYY-MM-DD 日期字符串格式化为 Figma 设计稿要求的 "MMM DD, YYYY" 格式。
 * 例如 "2026-04-15" → "Apr 15, 2026"
 */
export function formatGroupDate(dateKey: string): string {
  const d = dayjs(dateKey);
  if (!d.isValid()) return dateKey;
  return d.format('MMM DD, YYYY');
}
